<template>
  <div :class="classes(n(), isLiteapp ? n('--liteapp') : '')">
    <!-- 可滚动区域（header + content）- liteapp 环境使用 scroll-view -->
    <scroll-view
      v-if="isLiteapp"
      ref="scrollableRef"
      :class="classes(n('scrollable'))"
      scroll-y
      :show-scrollbar="false"
      @scroll="handleScroll"
    >
      <!-- 顶部区域 -->
      <div :class="classes(n('header'))">
        <!-- 图标区域 -->
        <div
          v-if="icon || $slots['header-icon']"
          :class="classes(n('icon'), icon ? n(`icon--${icon}`) : '')"
        >
          <template v-if="$slots['header-icon']">
            <slot name="header-icon" />
          </template>
          <component
            :is="iconConfig.component"
            v-else-if="iconConfig"
            width="128"
            :color="iconConfig.color"
          />
        </div>

        <!-- 标题区域 -->
        <h2
          v-if="title || $slots['header-title']"
          :class="classes(n('title'))"
        >
          <slot name="header-title">
            {{ title }}
          </slot>
        </h2>

        <!-- 描述区域 -->
        <div
          v-if="description || $slots['header-description']"
          :class="classes(n('description'))"
        >
          <slot name="header-description">
            {{ description }}
          </slot>
        </div>
      </div>

      <!-- 中间内容区域 -->
      <div
        v-if="$slots.content"
        :class="classes(n('content'))"
      >
        <slot name="content" />
      </div>
    </scroll-view>

    <!-- 可滚动区域（header + content）- H5 环境使用原生 div -->
    <div
      v-else
      ref="scrollableRef"
      :class="classes(n('scrollable'))"
      @scroll="handleScroll"
    >
      <!-- 顶部区域 -->
      <div :class="classes(n('header'))">
        <!-- 图标区域 -->
        <div
          v-if="icon || $slots['header-icon']"
          :class="classes(n('icon'), icon ? n(`icon--${icon}`) : '')"
        >
          <template v-if="$slots['header-icon']">
            <slot name="header-icon" />
          </template>
          <component
            :is="iconConfig.component"
            v-else-if="iconConfig"
            width="128"
            :color="iconConfig.color"
          />
        </div>

        <!-- 标题区域 -->
        <h2
          v-if="title || $slots['header-title']"
          :class="classes(n('title'))"
        >
          <slot name="header-title">
            {{ title }}
          </slot>
        </h2>

        <!-- 描述区域 -->
        <div
          v-if="description || $slots['header-description']"
          :class="classes(n('description'))"
        >
          <slot name="header-description">
            {{ description }}
          </slot>
        </div>
      </div>

      <!-- 中间内容区域 -->
      <div
        v-if="$slots.content"
        :class="classes(n('content'))"
      >
        <slot name="content" />
      </div>
    </div>

    <!-- 底部区域（固定在底部） -->
    <div
      v-if="hasFooter"
      :class="classes(n('footer'), hasScrollableContent ? n('footer--scrollable') : '')"
    >
      <!-- 渐变蒙层 - 提示上方内容可滚动 -->
      <div
        v-if="showGradient"
        :class="classes(n('footer-gradient'))"
      />

      <!-- 用户协议区域 -->
      <div
        v-if="$slots['footer-top']"
        :class="classes(n('footer-top'))"
      >
        <slot name="footer-top" />
      </div>

      <!-- 操作按钮区域 -->
      <div
        v-if="$slots['footer-actions']"
        :class="classes(n('actions'))"
      >
        <slot name="footer-actions" />
      </div>

      <!-- 底部信息区域容器 -->
      <div :class="classes(n('footer-info'))">
        <!-- 额外信息区域 -->
        <div
          v-if="$slots['footer-extra']"
          :class="classes(n('footer-extra'))"
        >
          <slot name="footer-extra" />
        </div>

        <!-- 底部固定区域 -->
        <div
          v-if="$slots['footer-bottom']"
          :class="classes(n('footer-bottom'))"
        >
          <slot name="footer-bottom" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, onMounted, nextTick } from 'virtual-vue';
import { props } from './props';
import { emits } from './emits';
import { createNamespace } from '../utils/components';
import { DoneIcon, InfoFIcon, ErrorIcon, TimeIcon } from '@tencent/wphk-icon-vue';
import { throttle } from '../shared/function';
import platform, { PlatformEnum } from '@tencent/wphk-platform-adapter';

const { name, n, classes } = createNamespace('result');

export default defineComponent({
  name,
  components: {
    DoneIcon,
    InfoFIcon,
    ErrorIcon,
    TimeIcon,
  },
  props,
  emits,
  setup(props, { slots }) {
    const scrollableRef = ref<HTMLElement>();
    const showGradient = ref(false);
    const hasScrollableContent = ref(false);

    // Liteapp 环境判断
    const isLiteapp = platform.platformType === PlatformEnum.LiteApp;

    // 判断是否有底部内容
    const hasFooter = computed(() => !!(
      slots['footer-top']
      || slots['footer-actions']
      || slots['footer-extra']
      || slots['footer-bottom']
    ));

    // 获取图标组件配置（包含组件和颜色）
    const iconConfig = computed(() => {
      const iconMap: Record<string, { component: any; color: string }> = {
        success: { component: DoneIcon, color: '#07C160' },
        info: { component: InfoFIcon, color: '#10AEFF' },
        danger: { component: ErrorIcon, color: '#FA5151' },
        warning: { component: ErrorIcon, color: '#FFC300' },
        waiting: { component: TimeIcon, color: '#10AEFF' },
      };
      return props.icon ? iconMap[props.icon] : null;
    });

    // 检测容器是否可滚动及滚动位置
    // 当内容高度超过可视高度且未滚动到底部时，显示渐变蒙层
    const checkScroll = () => {
      if (scrollableRef.value && hasFooter.value) {
        const { scrollHeight, clientHeight, scrollTop } = scrollableRef.value;
        // 内容是否可以滚动
        const canScroll = scrollHeight > clientHeight + 1;
        // 是否已经滚动到底部（添加1px的容差）
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
        // 记录内容是否可滚动（用于控制 footer-top 的 margin）
        hasScrollableContent.value = canScroll;
        // 只有当内容可滚动且未到达底部时，才显示渐变蒙层
        showGradient.value = canScroll && !isAtBottom;
      }
    };

    // 处理滚动事件（使用节流限制执行频率，默认 200ms）
    const handleScroll = throttle(() => {
      // 滚动时也检查一下，确保状态正确
      checkScroll();
    }, 200);

    onMounted(() => {
      // 初始化时检查一次，确保页面加载时就能正确显示渐变蒙层
      nextTick(() => {
        checkScroll();
      });
    });

    return {
      n,
      classes,
      scrollableRef,
      showGradient,
      hasScrollableContent,
      hasFooter,
      iconConfig,
      handleScroll,
      isLiteapp,
    };
  },
});
</script>

<style lang="less">
@import './result.less';
</style>
